// Utilidades para normalización de valores (fechas, etc.) provenientes de Autocomplete
import { LIEN_LIST_DATA } from '@pages/DocumentAuditPage/modules/DocumentTaskDetail/components/DocumentReview/DocumentForms/ApproveForm/constants';

/**
 * Mapea valores del modelo de lenguaje a valores esperados por el formulario
 */
const VALUE_MAPPINGS: Record<string, Record<string, string>> = {
  hasGarage: {
    no: 'No aplica',
    'no aplica': 'No aplica',
    'no-aplica': 'No aplica',
    no_aplica: 'No aplica',
    si: 'Si',
    sí: 'Si',
    yes: 'Si',
    true: 'Si',
    false: 'No aplica',
    '1': 'Si',
    '0': 'No aplica',
  },
  hasAdmin: {
    no: 'No aplica',
    'no aplica': 'No aplica',
    'no-aplica': 'No aplica',
    no_aplica: 'No aplica',
    si: 'Si',
    sí: 'Sí',
    yes: 'Si',
    true: 'Si',
    false: 'No aplica',
    1: 'Si',
    0: 'No aplica',
  },
  hasTerrace: {
    no: 'No aplica',
    'no aplica': 'No aplica',
    'no-aplica': 'No aplica',
    no_aplica: 'No aplica',
    si: 'Si',
    sí: 'Si',
    yes: 'Si',
    true: 'Si',
    false: 'No aplica',
    '1': 'Si',
    '0': 'No aplica',
  },
  parkingWithDoubleKeyOrDeed: {
    no: 'No',
    si: 'Si',
    sí: 'Si',
    yes: 'Si',
    true: 'Si',
    false: 'No',
    '1': 'Si',
    '0': 'No',
  },
  areaType: {
    private: 'private',
    built: 'built',
    'area construida': 'built',
    'area construido': 'built',
    construida: 'built',
    construido: 'built',
    terreno: 'private',
    lote: 'private',
    privado: 'private',
    privada: 'private',
  },
  propertyType: {
    casa: 'Casa',
    apartamento: 'Apartamento',
    apto: 'Apartamento',
    departamento: 'Apartamento',
  },
  liensCategory: {
    complex_patrimony: 'complex_patrimony',
    'patrimonio complejo': 'complex_patrimony',
    'patrimonio complejo (hijos menores)': 'complex_patrimony',
    complex_lien: 'complex_lien',
    complejo: 'complex_lien',
    'complejo (sucesión, embargo, prohibición, leasing)': 'complex_lien',
    simple_lien: 'simple_lien',
    simple: 'simple_lien',
    'simple (hipoteca, hipoteca + patrimonio simple, patrimonio simple)': 'simple_lien',
    free_of_lien: 'free_of_lien',
    libre: 'free_of_lien',
    'libre de gravámenes': 'free_of_lien',
    'libre (libre de gravámenes, ninguno)': 'free_of_lien',
    ninguno: 'free_of_lien',
  },
  // Mapeo de gravámenes individuales (valores que coinciden con LIEN_LIST)
  liens: {
    Ninguno: 'none',
    'Libre de Gravámenes ó Afectación familiar': 'lien_free_family_affectation',
    'Patrimonio simple (con hijos mayores o sin hijos)':
      'family_patrimony_with_adult_children',
    'Patrimonio complejo (con hijos menores)':
      'family_patrimony_with_minor_children',
    Patrimonio: 'assets',
    'Hipoteca ó Hipoteca + Afectación Familiar': 'mortgage_family',
    'Hipoteca + Patrimonio con hijos': 'mortgage_children_assets',
    'Hipoteca + Patrimonio': 'mortgage_assets',
    Embargo: 'embargo',
    Leasing: 'leasing',
    'Liquidación de Herencia': 'inheritance_settlement',
    'En trámite': 'in_process',
    'Afectación familia': 'family_affectation',
    Hipoteca: 'mortgage',
    'Prohibición transferencia del dominio': 'transfer_prohibition',
    'Derecho de preferencia': 'preference_right',
    'Pacto de retroventa': 'repurchase_agreement',
    Valorización: 'valuation',
    Sucesión: 'inheritance',
    'Crédito Hipotecario': 'mortgage_credit',
    'Patrimonio familiar': 'family_patrimony',
    Otro: 'others',
    Subsidio: 'subsidy',
    'Subsidio Infonavit': 'subsidy_infonavit',
    'Subsidio Fovissste': 'subsidy_fovissste',
    'Crédito INVI beneficio de vivienda CDMX': 'invi_cdmx_credit',
    'Crédito hipotecario ISFAM': 'isfam_credit',
    'Adjudicaciones procedentes de gravámenes': 'adjudication_lien',
    'Adjudicaciones procedentes de remate': 'adjudication_auction',
  },
};

// Conjunto estricto de códigos válidos de gravámenes (posición 1 de LIEN_LIST_DATA)
const ALLOWED_LIENS = new Set<string>(
  LIEN_LIST_DATA?.map(([, value]) => value)
);

/**
 * Convierte una fecha de formatos comunes a formato aceptado por inputs HTML date (YYYY-MM-DD).
 * - Soporta DD-MM-YYYY y DD/MM/YYYY
 * - Si viene en ISO con tiempo (YYYY-MM-DDTHH:mm:ssZ), recorta a YYYY-MM-DD
 */
export const normalizeDateForHtmlInput = (value: string): string => {
  if (!value) return value;
  // ISO con tiempo
  if (value.length > 10 && value.includes('T')) {
    return value.slice(0, 10);
  }
  // DD-MM-YYYY o DD/MM/YYYY
  const match = value.match(/^(\d{2})[\/-](\d{2})[\/-](\d{4})$/);
  if (match) {
    const [, dd, mm, yyyy] = match;
    return `${yyyy}-${mm}-${dd}`;
  }
  return value;
};

/**
 * Normaliza valores numéricos del área redondeando al entero más cercano
 */
const normalizeAreaValue = (value: string): string => {
  if (!value) return value;

  // Extraer números del string (puede venir con "m²", "m2", etc.)
  const numericMatch = value.match(/(\d+(?:\.\d+)?)/);
  if (!numericMatch) return value;

  const numericValue = parseFloat(numericMatch[1]);
  if (isNaN(numericValue)) return value;

  // Redondear al entero más cercano
  return Math.round(numericValue).toString();
};

/**
 * Normaliza valores numéricos que deben ser enteros sin decimales (elevator, yearsOld)
 */
const normalizeIntegerValue = (value: string): string => {
  if (!value) return value;

  const numericValue = parseFloat(value);
  if (isNaN(numericValue)) return value;

  // Redondear al entero más cercano
  return Math.round(numericValue).toString();
};

/**
 * Normaliza valores de gravámenes separados por comas a array de valores mapeados
 */
const normalizeLiensValue = (value: string) => {
  if (!value) return '';
  // Dividir por comas o punto y coma y limpiar espacios
  const liens = value.split(/[,;]/).map(lien => lien.trim());

  // Mapear cada gravamen individual
  const fieldMappings = VALUE_MAPPINGS.liens;
  const mappedLiens = liens.map(lien => fieldMappings[lien] || lien);
  // Filtrar valores vacíos, dejar solo códigos válidos, y devolver array único
  const filtered = mappedLiens.filter(lien => lien && ALLOWED_LIENS.has(lien));
  // Unir con punto y coma (formato esperado por MultiSelectDropdownField)
  const result = [...new Set(filtered)].join(';');
  return result;
};

/**
 * Mapea un valor del modelo de lenguaje al valor esperado por el formulario
 */
const mapValueToFormValue = (
  field: string,
  value: string | number
): string | number => {
  const fieldMappings = VALUE_MAPPINGS[field];
  if (!fieldMappings) return value;

  const stringValue = value?.toString();
  const normalizedValue = stringValue?.toLowerCase().trim();

  // Mantener 1 o 0 en vez de "Si"/"No aplica"
  if (field === 'hasAdmin' || field === 'hasTerrace') {
    if (['si', 'sí', 'yes', 'true', '1'].includes(normalizedValue)) return 1;
    if (
      ['no', 'no aplica', 'no-aplica', 'no_aplica', 'false', '0'].includes(
        normalizedValue
      )
    )
      return 0;
  }

  // Campos YES_NO de MX: convertir a 1 o 0
  if (field === 'parkingWithDoubleKeyOrDeed') {
    if (['si', 'sí', 'yes', 'true', '1'].includes(normalizedValue)) return 1;
    if (['no', 'false', '0'].includes(normalizedValue)) return 0;
  }

  return fieldMappings[normalizedValue] || value;
};

/**
 * Normaliza valores de Autocomplete antes de colocarlos en el formulario.
 * - Campos de fecha: aplica `normalizeDateForHtmlInput`
 * - Campos con mapeo: aplica `mapValueToFormValue`
 * - Campo área: aplica `normalizeAreaValue`
 * - Campo gravámenes: aplica `normalizeLiensValue`
 * - Otros campos: mantiene valor original
 */
export const normalizeAutocompleteValue = (field: string, value: string) => {
  if (!value) return field === 'liens' ? '' : value;

  // Campos de fecha que necesitan normalización
  const dateFields = new Set<string>(['constructionDate']);
  if (dateFields.has(field)) {
    return normalizeDateForHtmlInput(value);
  }

  // Campos que necesitan mapeo de valores
  const mappableFields = new Set<string>([
    'hasGarage',
    'hasAdmin',
    'hasTerrace',
    'areaType',
    'propertyType',
    'parkingWithDoubleKeyOrDeed',
    'liensCategory',
  ]);
  if (mappableFields.has(field)) {
    return mapValueToFormValue(field, value);
  }

  // Campo área que necesita redondeo
  const areaFields = new Set<string>(['area']);
  if (areaFields.has(field)) {
    return normalizeAreaValue(value);
  }

  // Campo gravámenes que necesita procesamiento especial
  const liensFields = new Set<string>(['liens']);
  if (liensFields.has(field)) {
    return normalizeLiensValue(value);
  }

  // Campos numéricos que deben ser enteros sin decimales (MX)
  const integerFields = new Set<string>(['elevator', 'yearsOld']);
  if (integerFields.has(field)) {
    return normalizeIntegerValue(value);
  }

  return value;
};
